'use client';

/**
 * Tooltip wraps its Root in its own TooltipProvider so it works outside the app
 * Providers tree, and forwards `delayDuration` to both so per-instance delays win
 * over an outer provider. Default delay is 100ms (Radix default is 700ms).
 */

import * as React from 'react';
import { Tooltip as TooltipPrimitive } from 'radix-ui';
import { Info } from 'lucide-react';

import { cn } from '@/lib/utils/utils';

const RadixTooltipProvider = TooltipPrimitive.Provider;
const RadixTooltipRoot = TooltipPrimitive.Root;

const DEFAULT_DELAY_MS = 100;

const TooltipProvider = ({
  delayDuration = DEFAULT_DELAY_MS,
  ...props
}: React.ComponentPropsWithoutRef<typeof RadixTooltipProvider>) => (
  <RadixTooltipProvider delayDuration={delayDuration} {...props} />
);

function Tooltip({
  delayDuration = DEFAULT_DELAY_MS,
  ...props
}: React.ComponentPropsWithoutRef<typeof RadixTooltipRoot>) {
  return (
    <TooltipProvider delayDuration={delayDuration}>
      <RadixTooltipRoot delayDuration={delayDuration} {...props} />
    </TooltipProvider>
  );
}
Tooltip.displayName = RadixTooltipRoot.displayName ?? 'Tooltip';

const TooltipTrigger = TooltipPrimitive.Trigger;

const TooltipContent = React.forwardRef<
  React.ElementRef<typeof TooltipPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Content>
>(({ className, sideOffset = 4, ...props }, ref) => (
  <TooltipPrimitive.Portal>
    <TooltipPrimitive.Content
      ref={ref}
      sideOffset={sideOffset}
      className={cn(
        'bg-popover text-popover-foreground border-border animate-in fade-in-0 zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 z-50 overflow-hidden rounded-md border px-3 py-1.5 text-xs shadow motion-reduce:animate-none',
        className
      )}
      {...props}
    />
  </TooltipPrimitive.Portal>
));
TooltipContent.displayName = TooltipPrimitive.Content.displayName;

type TooltipIconProps = {
  content: React.ReactNode;
  ariaLabel?: string;
  iconClassName?: string;
  side?: React.ComponentProps<typeof TooltipContent>['side'];
  align?: React.ComponentProps<typeof TooltipContent>['align'];
  sideOffset?: number;
  className?: string;
};

function TooltipIcon({
  content,
  ariaLabel = 'More information',
  iconClassName,
  side,
  align,
  sideOffset = 4,
  className
}: TooltipIconProps) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type='button'
          aria-label={ariaLabel}
          className='inline-flex items-center'
        >
          <Info
            className={cn(
              'text-muted-foreground h-3.5 w-3.5 cursor-help',
              iconClassName
            )}
          />
          <span className='sr-only'>{ariaLabel}</span>
        </button>
      </TooltipTrigger>
      <TooltipContent
        side={side}
        align={align}
        sideOffset={sideOffset}
        className={className}
      >
        {typeof content === 'string' ? (
          <p className='text-xs'>{content}</p>
        ) : (
          content
        )}
      </TooltipContent>
    </Tooltip>
  );
}

export {
  Tooltip,
  TooltipTrigger,
  TooltipContent,
  TooltipProvider,
  TooltipIcon
};
